/**
 * Pipelines data layer — single point of swap for the Pipelines feature.
 *
 * Static config is fetched once per pipeline via three parallel SWR calls and
 * composed into a `PipelineSnapshot` (the page gates on it):
 *   - `GET /api/pipelines/:id`           (Pipeline)
 *   - `GET /api/pipelines/:id/statuses`  (InternalStatus[])
 *   - `GET /api/managers`                (Manager[], the tenant's; participants derived)
 *
 * Lead data is NOT in the snapshot. As of SBO-48/52 the engine filters,
 * sorts, paginates, and aggregates server-side, so the console no longer holds
 * the full lead array:
 *   - `useStageLeads()`  → one page of leads for the table (`GET …/leads?…`),
 *     keyed on the live filter + sort + page state (zustand `useStageState`).
 *   - `useLeadStats()`   → server-computed aggregates (`GET …/leads/stats?…`)
 *     that drive the stage dashboard + side panels. Deduped by SWR so the four
 *     dashboard selectors share a single request per filter combination.
 *
 * Both reads derive their query from `useStageState` (the feature's UI store),
 * so filters/search/period narrow the table AND the dashboard together. Search
 * is debounced before it reaches the SWR key so each keystroke doesn't fire a
 * request. `keepPreviousData` holds the last result while a new key loads, so
 * paging/filtering doesn't flash an empty table.
 */
import { useMemo } from 'react'
import useSWR from 'swr'
import { participantsOf, useManagers } from '@/shared/hooks/useManagers.ts'
import { useWords } from '@/shared/hooks/useWords.ts'
import {
  managerDisplayName,
  type InternalStatus,
  type Lead,
  type LeadStats,
  type LeadStatsQuery,
  type ListLeadsPage,
  type ListLeadsQuery,
  type ManagerId,
  type Pipeline,
} from '@ludus/core'
import {
  PipelineApiError,
  fetchLeadStats,
  fetchPipeline,
  fetchPipelineLeads,
  fetchPipelineStatuses,
} from '@/lib/api/pipelines.ts'
import { funnelChannelFor } from '@/shared/utils/funnels.ts'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue.ts'
import { usePipelineId } from './PipelineIdContext.tsx'
import { useStageState } from '../PipelineView/useStageState.ts'
import {
  AGING_BUCKETS,
  type AgingBucket,
  type LeadView,
  type PipelineSnapshot,
  type StageMetrics,
} from '../types.ts'

// ──────────────────────────────────────────────────────────────────────────────
// Lead → LeadView projection
// ──────────────────────────────────────────────────────────────────────────────

function enrichLead(lead: Lead): LeadView {
  return {
    id: lead.id,
    externalRef: lead.externalRef ?? (lead.id as unknown as string),
    pipelineId: lead.pipelineId,
    statusId: lead.statusId,
    assignedManagerId: lead.assignedManagerId,
    funnelKey: lead.funnel,
    contactName: lead.name,
    company: '',
    value: lead.price ?? 0,
    probability: 0,
    tags: lead.tags,
    nextAction: null,
    lastTouchAt: lead.updatedAt ? lead.updatedAt.getTime() : null,
    createdAt: lead.createdAt.getTime(),
    assignedAt: lead.assignedAt ? lead.assignedAt.getTime() : null,
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Static-config SWR composition (pipeline / statuses / managers)
// ──────────────────────────────────────────────────────────────────────────────

interface PipelinesSourceState {
  snapshot: PipelineSnapshot | null
  isLoading: boolean
  error: PipelineApiError | null
}

export function usePipelinesSourceState(): PipelinesSourceState {
  const pipelineId = usePipelineId()
  const pipelineSwr = useSWR<Pipeline, PipelineApiError>(`/pipelines/${pipelineId}`, () =>
    fetchPipeline(pipelineId),
  )
  const statusesSwr = useSWR(`/pipelines/${pipelineId}/statuses`, () =>
    fetchPipelineStatuses(pipelineId),
  )
  const managersSwr = useManagers()

  const snapshot = useMemo<PipelineSnapshot | null>(() => {
    const pipeline = pipelineSwr.data
    const stages = statusesSwr.data
    const managers = managersSwr.managers
    if (!pipeline || !stages || !managers) return null
    return {
      pipeline,
      stages,
      // Engine has no lifecycle-kind map today; empty drives the position-based
      // palette fallback in `shared/utils/stageColors.ts`.
      stageKinds: {},
      managers,
      participants: participantsOf(managers, pipelineId),
    }
  }, [pipelineSwr.data, statusesSwr.data, managersSwr.managers, pipelineId])

  const error = firstError([pipelineSwr.error, statusesSwr.error, managersSwr.error])

  const isLoading =
    !snapshot && !error && (pipelineSwr.isLoading || statusesSwr.isLoading || managersSwr.isLoading)

  return { snapshot, isLoading, error }
}

function firstError(errors: Array<PipelineApiError | undefined>): PipelineApiError | null {
  for (const e of errors) {
    if (e) return e
  }
  return null
}

// ──────────────────────────────────────────────────────────────────────────────
// Public hooks — snapshot selectors (preserve the SBO-46 surface)
// ──────────────────────────────────────────────────────────────────────────────

export function usePipelinesSource(): { snapshot: PipelineSnapshot } {
  const { snapshot } = usePipelinesSourceState()
  if (!snapshot) {
    throw new Error(
      'usePipelinesSource: snapshot not ready. Render this hook under a gate that defers mount until `usePipelinesSourceState().snapshot` is non-null.',
    )
  }
  return { snapshot }
}

export function usePipelineStages() {
  return usePipelinesSource().snapshot.stages
}

export function usePipelineStageKinds() {
  return usePipelinesSource().snapshot.stageKinds
}

export function usePipelineMeta() {
  return usePipelinesSource().snapshot.pipeline
}

/** `t` in the words of this pipeline's kind (#66). */
export function usePipelineWords() {
  return useWords(usePipelineMeta().kind)
}

/** A board of interactions carries no value: no column, no totals, no price. */
export function usePipelineShowsValue() {
  return usePipelineMeta().kind !== 'interaction'
}

export function usePipelineManagers() {
  return usePipelinesSource().snapshot.participants
}

export function useTenantManagers() {
  return usePipelinesSource().snapshot.managers
}

// ──────────────────────────────────────────────────────────────────────────────
// Server-backed reads — derive their query from the live UI state
// ──────────────────────────────────────────────────────────────────────────────

const SEARCH_DEBOUNCE_MS = 300

/**
 * The shared filter slice (statusId / managerIds / period / q) pulled from the
 * UI store, with the search box debounced. Drives both the table and the stats
 * SWR keys, so filters narrow the dashboard and the table in lockstep.
 */
function useLeadFilters(): LeadStatsQuery {
  const selectedStageId = useStageState((s) => s.selectedStageId)
  const filters = useStageState((s) => s.filters)
  const debouncedSearch = useDebouncedValue(filters.search, SEARCH_DEBOUNCE_MS)
  const trimmed = debouncedSearch.trim()

  return useMemo(
    () => ({
      statusId: selectedStageId,
      managerIds: filters.managerIds,
      period: filters.period,
      q: trimmed.length > 0 ? trimmed : undefined,
    }),
    [selectedStageId, filters.managerIds, filters.period, trimmed],
  )
}

export interface StageLeadsResult {
  leads: LeadView[]
  total: number
  isLoading: boolean
  error: PipelineApiError | null
}

/**
 * One server-paginated page of leads for the table. Reads filter/sort/page from
 * `useStageState`; the SWR key is the full query, so any change refetches the
 * matching page. `keepPreviousData` avoids an empty-table flash between pages.
 */
export function useStageLeads(): StageLeadsResult {
  const pipelineId = usePipelineId()
  const filters = useLeadFilters()
  const sort = useStageState((s) => s.sort)
  const page = useStageState((s) => s.page)
  const pageSize = useStageState((s) => s.pageSize)

  const query: ListLeadsQuery = {
    ...filters,
    sort: sort.key,
    dir: sort.direction,
    limit: pageSize,
    offset: page * pageSize,
  }

  const { data, isLoading, error } = useSWR<ListLeadsPage<Lead>, PipelineApiError>(
    ['leads', pipelineId, query],
    () => fetchPipelineLeads(pipelineId, query),
    { keepPreviousData: true },
  )

  const leads = useMemo(() => (data?.items ?? []).map(enrichLead), [data])
  return { leads, total: data?.total ?? 0, isLoading, error: error ?? null }
}

/**
 * Server-computed aggregates for the dashboard + side panels. Keyed on the
 * filter slice only (no sort/paging), so the four dashboard selectors below
 * share a single deduped request per filter combination.
 */
export function useLeadStats(): LeadStats | null {
  const pipelineId = usePipelineId()
  const filters = useLeadFilters()
  const { data } = useSWR(
    ['leadStats', pipelineId, filters],
    () => fetchLeadStats(pipelineId, filters),
    { keepPreviousData: true },
  )
  return data ?? null
}

export interface LeadStatsStatus {
  isLoading: boolean
  error: PipelineApiError | null
}

/**
 * Loading/error surface for the stats read, exposed without touching the five
 * selector signatures that consume `useLeadStats`. It subscribes to the SAME
 * SWR key (`['leadStats', pipelineId, filters]`), so SWR dedupes the request —
 * this adds no network call — and the board can render an error/loading state
 * for the dashboard instead of silently showing zeroes on a failed fetch.
 */
export function useLeadStatsStatus(): LeadStatsStatus {
  const pipelineId = usePipelineId()
  const filters = useLeadFilters()
  const { isLoading, error } = useSWR<LeadStats, PipelineApiError>(
    ['leadStats', pipelineId, filters],
    () => fetchLeadStats(pipelineId, filters),
    { keepPreviousData: true },
  )
  return { isLoading, error: error ?? null }
}

export interface PipelineTotals {
  totalLeads: number
  totalValue: number
}

/** Pipeline-wide (filtered) totals for the header — ignores the selected stage. */
export function usePipelineTotals(): PipelineTotals {
  const stats = useLeadStats()
  return { totalLeads: stats?.total ?? 0, totalValue: stats?.totalValue ?? 0 }
}

export function usePipelineMetrics(): StageMetrics[] {
  const stages = usePipelineStages()
  const stats = useLeadStats()
  return useMemo(() => buildStageMetrics(stages, stats), [stages, stats])
}

export interface OwnerLoad {
  managerId: ManagerId
  managerName: string
  leadCount: number
  totalValue: number
}

export function useStageOwners(): OwnerLoad[] {
  const managers = useTenantManagers()
  const stats = useLeadStats()
  return useMemo(() => {
    const nameById = new Map(managers.map((m) => [m.id, managerDisplayName(m)]))
    return (stats?.byOwner ?? [])
      .map((o) => ({
        managerId: o.managerId,
        managerName: nameById.get(o.managerId) ?? o.managerId,
        leadCount: o.count,
        totalValue: o.totalValue,
      }))
      .sort((a, b) => b.leadCount - a.leadCount)
  }, [managers, stats])
}

export interface FunnelLoad {
  key: string
  label: string
  abbr: string
  color: string
  leadCount: number
}

export function useStageFunnels(): FunnelLoad[] {
  const stats = useLeadStats()
  return useMemo(
    () =>
      (stats?.byFunnel ?? [])
        .map((f) => ({ ...funnelChannelFor(f.funnel), leadCount: f.count }))
        .sort((a, b) => b.leadCount - a.leadCount),
    [stats],
  )
}

export function useStageAging(): Array<{ bucket: AgingBucket; count: number }> {
  const stats = useLeadStats()
  return useMemo(() => {
    const counts = new Map(stats?.byAge.map((a) => [a.bucket, a.count]) ?? [])
    return AGING_BUCKETS.map((b) => ({ bucket: b, count: counts.get(b) ?? 0 }))
  }, [stats])
}

// ──────────────────────────────────────────────────────────────────────────────
// Stage metrics from the server `byStage` aggregate
// ──────────────────────────────────────────────────────────────────────────────

function clampPct(v: number): number {
  return Math.max(0, Math.min(100, v))
}

/**
 * Build per-stage metrics from `stats.byStage`. Count / totalValue / median /
 * stuck come straight from the server; the forward/backward conversion ratios
 * are the simple adjacent-stage count ratio (placeholder until the engine
 * aggregates real `status_changed` history) — same formula as the old
 * client-side `computeStageMetrics`.
 */
function buildStageMetrics(
  stages: readonly InternalStatus[],
  stats: LeadStats | null,
): StageMetrics[] {
  const byStage = new Map(stats?.byStage.map((s) => [s.statusId, s]) ?? [])
  const countAt = (idx: number) => byStage.get(stages[idx].id)?.count ?? 0

  return stages.map((stage, idx) => {
    const row = byStage.get(stage.id)
    const leadCount = row?.count ?? 0
    const prevCount = idx > 0 ? countAt(idx - 1) : null
    const nextCount = idx < stages.length - 1 ? countAt(idx + 1) : null

    const fromPrev =
      prevCount && prevCount > 0 ? clampPct(Math.round((leadCount / prevCount) * 100)) : null
    const toNext =
      nextCount !== null && leadCount > 0
        ? clampPct(Math.round((nextCount / leadCount) * 100))
        : null

    return {
      stageId: stage.id,
      leadCount,
      totalValue: row?.totalValue ?? 0,
      medianAgeDays: row?.medianAgeDays ?? 0,
      stuckCount: row?.stuckCount ?? 0,
      fromPrev,
      toNext,
    }
  })
}
